<template>
  <div class="wrapper">
    <Header
      :name="$t('app.spaces.spacesView.header')"
    />
    <div class="data-card card">
      <div class="button-header">
        <Button :disabled="!canWriteCampaignAndSpaces" class="icon icon-left primary light-text" :label="$t('app.spaces.spacesView.createSpace')" @click="state.modalIsOpen = true; state.modalAction = 'create';">
          <template #prefix>
            <PlusIcon style="width: 16px;"></PlusIcon>
          </template>
        </Button>
        <VField class="light put-to-end search" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
      </div>
      <transition name="fade">
        <Table
          class="spaces-table"
          v-if="spaces?.length"
          :data="sortedSpaces"
          :fields="tableFields"
          :search="state.search"
          :removeBulkActions="true"
          :rowClickToSelect="false"
          @clickOnRow="
            (space: Space) =>
              $router.push({
                name: 'space',
                params: {
                  spaceId: space.workspace_id,
                },
              })
          "
          :itemsPerPage="100"
        >
          <template #[`name`]="space: { data: Space }">
            <div class="container">
              <img class="logo" :src="space?.data.image_id ? getImage(space.data.image_id) : getRandomSpacePicture(space.data.name)" alt="Logo" />
              <span>{{ space.data.name }}</span>
            </div>
          </template>
          <template #[`campaigns`]="space: { data: Space }">
            <div class="container">
              <span class="number" v-text="getNumberOfCampaigns(space.data.workspace_id)" />
            </div>
          </template>
          <template #[`created_at`]="space: { data: Space }">
            <div class="container">
              <span v-text="space.data.creation_time ? getDate(space.data.creation_time, 'LL') : $t('app.campaign.results.noDate')" />
            </div>
          </template>
          <template #actions="space: { row: Space }">
            <Button :disabled="!canWriteCampaignAndSpaces" class="icon icon-left outline-white" icon="bi-gear" :label="$t('app.spaces.spaceView.campaignSettings')" @click="editSpace(space.row)" />
          </template>
        </Table>
      </transition>
    </div>
    <SpaceModal :isOpen="state.modalIsOpen" :space="state.space" :action="state.modalAction" @close="((state.modalIsOpen = false), (state.space = null))" />
  </div>
</template>

<style lang="scss" scoped>

.data-card {
  flex: 1;

  .button-header {
    display: flex;
    flex-direction: row;
    gap: 8px;

    .put-to-end {
      margin-left: auto;
    }
  }

  .search-container {
   display: flex;
   width: 100%;
  
   .input-wrapper {
    max-width: 250px;
    margin-left: auto;
   }
  }
  
  ::v-deep(.table-wrapper) {
    margin-top: 16px;
  }
}

.search {
  width: 350px;
}

.center-field {
  justify-content: center !important;
}

.logo {
  width: 32px;
}
</style>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useQueries } from "@tanstack/vue-query";

import { getImage, getRandomSpacePicture } from "@/composables/getImage.js";
import { usePermissions } from "@/composables/permissions";

import Header from "@/components/Header.vue";
import SpaceModal from "@/components/modals/app/SpaceModal.vue";
import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';

import { campaignsApi } from "@/api/campaigns.api";
import { useSpaces } from "@/queries/space.query";
import { Space } from "@/types/space";
import { getDate } from "@/composables/utils";

const { t } = useI18n();
const route = useRoute();
const { canWriteCampaignAndSpaces } = usePermissions();
const { data: spaces, isPending: isSpacesPending } = useSpaces();

const state = reactive({
  modalAction: "",
  modalIsOpen: false,
  space: null,
  campaignsLoad: true,
  stats: [],
  search: {
    value: "",
    searchable: [
      "name",
    ],
  },
});

const tableFields = computed(() => {
  return [
    {
      id: "name",
      label: t("app.spaces.spacesView.spaceName"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "campaigns",
      label: t("app.spaces.spacesView.campaignCount"),
    },
    {
      id: "created_at",
      label: t("app.spaces.spacesView.createdAt"),
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b.creation_time, a.creation_time) ? 1 : -1;
        } else {
          return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
        }
      },
    },
  ];
});

const queries = computed(() => {
  if (isSpacesPending.value) return [];
  return spaces.value.map((space) =>  {
    return {
      queryKey: ["campaigns", { organisationId: route.params.companyId as string, spaceId: space.workspace_id }],
      queryFn: () => campaignsApi.getAll(route.params.companyId as string, space.workspace_id),
    }
  });
})

const spaceCampaignsQueries = useQueries({ queries: queries });

const sortedSpaces = computed(() => {
  if (isSpacesPending.value) return [];
  return [...spaces.value].sort((a, b) => a.name.localeCompare(b.name));
});

function editSpace(space: Space) {
  state.modalAction = 'edit';
  state.space = { ...space };
  state.modalIsOpen = true;
}

function getNumberOfCampaigns(spaceId: string) {
  if (!spaceCampaignsQueries?.value || spaceCampaignsQueries?.value.length === 0) return 0;
  const campaigns = spaceCampaignsQueries.value.filter((v) => !v.isPending && v.data.some((v) => v.campaign.parent_workspace_id === spaceId)).flatMap((v) => v.data);
  return campaigns.length || 0;
}

</script>
